import { useApolloClient, useMutation } from "@apollo/client";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { buildWasteStreamRegistrationTitle } from "encamp-shared/src/submissions/wasteStreamRegistration";
import {
  WasteClassification,
  WasteStreamRegistrationMatchesQuery,
} from "generated-graphql/graphql";
import { useContext, useMemo, useState } from "react";
import { refetchWasteStreamGridQueries } from "routes/Customer/Waste/refetchWasteStreamGridQueries";
import {
  CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION,
  CREATE_WASTE_STREAM_REGISTRATION_UPDATE_SUBMISSION_MUTATION,
} from "routes/Customer/Waste/WasteStreams/api";
import { openInNewTabFromPromise } from "util/download";

import { formatWasteStreamDotDescription } from "../Facilities/Facility/formatWasteStreamDotDescription";
import { TenantWasteFacilityProvider } from "../Facilities/Facility/TenantWasteFacilityProvider";
import { WasteFacilityStateContext } from "../Facilities/Facility/WasteFacilityContext";
import { WasteStreamMergeDialog } from "../Facilities/Facility/WasteStreamsDataGrid/WasteStreamMerge/WasteStreamMergeDialog";
import {
  LINK_WASTE_STREAM_TO_REGISTRATION_MUTATION,
  useWasteStreamDotDescription,
  useWasteStreamRegistrationMatches,
} from "./api";
import { MatchTab } from "./MatchTab";
import { RegisterNewTab } from "./RegisterNewTab";
import { WasteStreamSummaryFields } from "./WasteStreamSummaryFields";

type Tab = "match" | "new";

type Match = WasteStreamRegistrationMatchesQuery["wasteStreamRegistrationMatches"][number];
type MergeTarget = NonNullable<Match["relatedStream"]>;

export interface StateRegistrationDialogStream {
  id: string;
  name: string;
  // The grid-info fields shown in the current-stream summary box. Optional and
  // nullable so both grids' generated row types satisfy the shape; the dialog
  // is always handed a full grid row.
  eManifestProfileNumber?: string | null;
  vendorProfileNumber?: string | null;
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  formCode?: string | null;
  sourceCode?: string | null;
}

export interface StateRegistrationDialogProps<Row extends StateRegistrationDialogStream> {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  facilityName: string;
  state: string;
  /** Tenant owning the stream — used to build the post-create submission link. */
  tenantId: string;
  /**
   * The stream this dialog was opened for. Forwarded to the merge wizard as
   * its second (losing) row — full grid row objects flow through untouched,
   * since `WasteStreamMergeDialog.selectedRows` is loosely typed.
   */
  wasteStream: Row;
  /**
   * ISO YYYY-MM-DD bounds of the caller's active date filter. Only needed by
   * callers outside a facility page (the tenant-wide grid) — they scope the
   * merge wizard's queries and its "not just the selected time range" note.
   */
  startDate?: string;
  endDate?: string;
}

/**
 * The merge wizard reads its facility scope (effective facility id, active date
 * range) from `WasteFacilityContext`. The per-facility Waste Streams grid
 * renders this dialog inside that provider; the tenant-wide grid has no
 * facility page around it, so supply the same context from the props this
 * dialog already has rather than letting the wizard throw.
 */
function WithWasteFacilityScope({
  facilityId,
  startDate,
  endDate,
  children,
}: {
  facilityId: string;
  startDate?: string;
  endDate?: string;
  children: React.ReactNode;
}) {
  const ambientScope = useContext(WasteFacilityStateContext);
  if (ambientScope) return <>{children}</>;
  return (
    <TenantWasteFacilityProvider facilityId={facilityId} startDate={startDate} endDate={endDate}>
      {children}
    </TenantWasteFacilityProvider>
  );
}

/**
 * The window the grid computed this stream's flags with: passed in by the
 * tenant-wide grid, read from the facility page's filters otherwise.
 */
function useGridWindow(startDate?: string, endDate?: string) {
  const filters = useContext(WasteFacilityStateContext)?.wasteFacilityState.deferredFilters;
  return { startDate: startDate ?? filters?.startDate, endDate: endDate ?? filters?.endDate };
}

/**
 * The stream's grid info above the tabs, for context while matching. Renders
 * inside `WithWasteFacilityScope`, whose filters the DOT query shares with the
 * stream drawer.
 */
function CurrentStreamSummary({
  wasteStream,
  facilityName,
  state,
}: {
  wasteStream: StateRegistrationDialogStream;
  facilityName: string;
  state: string;
}) {
  const { data, loading } = useWasteStreamDotDescription(wasteStream.id);
  const dotDescription = formatWasteStreamDotDescription(data?.wasteStream ?? {}).text;

  return (
    <Box
      role="region"
      aria-label="Waste stream summary"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        backgroundColor: "background.default",
      }}
    >
      <Typography variant="subtitle2">{wasteStream.name}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {facilityName} · {state}
      </Typography>
      <WasteStreamSummaryFields
        data={wasteStream}
        dotDescription={dotDescription}
        dotDescriptionLoading={loading}
      />
    </Box>
  );
}

/**
 * The confirm button names the extra step a selection commits the user to: a
 * merge target takes precedence over an update, since it replaces linking
 * entirely.
 */
function confirmLabel(
  selectedMergeTarget: MergeTarget | undefined,
  selectedNeedsUpdate: boolean
): string {
  if (selectedMergeTarget) return "Merge streams";
  if (selectedNeedsUpdate) return "Confirm match + Create update submission";
  return "Confirm match";
}

/**
 * Owns the Match tab's confirm action: merge (deep-link to the merge wizard),
 * plain link, or link-then-create-update-submission when the selected
 * registration doesn't cover every code the stream ships. Pulled out of the
 * dialog component to keep that component under the line-count ratchet.
 */
function useConfirmMatchAction<Row extends StateRegistrationDialogStream>({
  wasteStream,
  facilityId,
  tenantId,
  gridWindow,
  matches,
  selectedRegistrationId,
  selectedMergeTarget,
  setMergeRows,
  handleClose,
  alerts,
  client,
}: {
  wasteStream: Row;
  facilityId: string;
  tenantId: string;
  gridWindow: { startDate?: string; endDate?: string };
  matches: Match[];
  selectedRegistrationId: string | null;
  selectedMergeTarget: MergeTarget | undefined;
  setMergeRows: (rows: Array<Row | MergeTarget>) => void;
  handleClose: () => void;
  alerts: ReturnType<typeof useAlerts>;
  client: ReturnType<typeof useApolloClient>;
}) {
  const selectedMatch = selectedRegistrationId
    ? matches.find((match) => match.registration.id === selectedRegistrationId)
    : undefined;
  // The registration is behind this stream's codes, so confirming the match
  // leaves work the state has to be told about. A merge target takes
  // precedence — folding this stream into the other one is a different action.
  const selectedNeedsUpdate =
    !selectedMergeTarget && (selectedMatch?.extraStreamCodes.length ?? 0) > 0;

  const [linkRegistration] = useMutation(LINK_WASTE_STREAM_TO_REGISTRATION_MUTATION);
  const [createUpdateSubmission] = useMutation(
    CREATE_WASTE_STREAM_REGISTRATION_UPDATE_SUBMISSION_MUTATION
  );
  // Covers the whole handler, not just the mutations — link, then possibly
  // create, then refetch, then navigate all have to finish before the button
  // re-enables, or it flickers live in the gap and invites a double-submit.
  const [confirmingMatch, setConfirmingMatch] = useState(false);

  const handleConfirmMatch = async () => {
    if (!selectedRegistrationId) return;
    // The selected registration already owns a stream — fold this stream into
    // it via the existing merge wizard rather than adding a second link.
    if (selectedMergeTarget) {
      // Target/winner first, this stream second — matches
      // WasteStreamMergeDialog's convention of keeping the first row's FK.
      setMergeRows([selectedMergeTarget, wasteStream]);
      return;
    }
    setConfirmingMatch(true);

    // Link, and — when the match needs it — create the update submission,
    // resolving to the URL to open (or undefined on any failure). Kept as one
    // function so it can be handed to `openInNewTabFromPromise` below without
    // duplicating the link-then-create ordering or the split error handling.
    const linkAndMaybeCreateUpdate = async (): Promise<string | undefined> => {
      try {
        // Linking first is load-bearing: the update-submission mutation refuses
        // a stream with no registrationId.
        await linkRegistration({
          variables: { wasteStreamId: wasteStream.id, registrationId: selectedRegistrationId },
        });
      } catch (e) {
        alerts.error("Failed to link the waste stream to that registration.", e);
        return undefined;
      }

      if (!selectedNeedsUpdate) {
        await refetchWasteStreamGridQueries(client, facilityId);
        alerts.success("Waste stream linked to the registration.");
        handleClose();
        return undefined;
      }

      // Past the link, so a failure here is the submission's alone. Reporting it
      // as a failed link would send the user back to relink a stream that is
      // already linked.
      try {
        const result = await createUpdateSubmission({
          variables: { wasteStreamId: wasteStream.id, ...gridWindow },
        });
        const submissionId =
          result.data?.createWasteStreamRegistrationUpdateSubmission.submissionId;
        await refetchWasteStreamGridQueries(client, facilityId);
        alerts.success("Waste stream linked. Update submission created.");
        handleClose();
        return submissionId ? `/o/${tenantId}/waste/submissions/${submissionId}/edit` : undefined;
      } catch (e) {
        // The link landed, so the grid has to be refetched anyway or it keeps
        // showing this stream as unlinked.
        await refetchWasteStreamGridQueries(client, facilityId);
        alerts.error("Waste stream linked, but creating the update submission failed.", e);
        return undefined;
      }
    };

    try {
      if (selectedNeedsUpdate) {
        // Reserve the tab synchronously, in this click's gesture — link,
        // create, and refetch below all await, so a `window.open` fired after
        // them lands too late and gets popup-blocked in Safari.
        await openInNewTabFromPromise(linkAndMaybeCreateUpdate);
      } else {
        await linkAndMaybeCreateUpdate();
      }
    } finally {
      setConfirmingMatch(false);
    }
  };

  return { handleConfirmMatch, confirmingMatch, selectedNeedsUpdate };
}

/**
 * Unified staff-only "+ State Registration" dialog — replaces the grid's
 * former per-stream "+ Submission" button. Two tabs: Match existing (default,
 * shown first) and Register new. A summary box above the tabs shows the
 * stream's grid info for context.
 *
 * The Match tab lists ranked registration candidates. A candidate that already
 * owns a waste stream is marked MERGE: confirming it folds this stream into
 * that one through the existing merge wizard, so a registration never ends up
 * with two unmerged streams. A bare candidate is linked directly.
 */
export function StateRegistrationDialog<Row extends StateRegistrationDialogStream>({
  open,
  onClose,
  facilityId,
  facilityName,
  state,
  tenantId,
  wasteStream,
  startDate,
  endDate,
}: StateRegistrationDialogProps<Row>) {
  const alerts = useAlerts();
  const client = useApolloClient();

  const [tab, setTab] = useState<Tab>("match");

  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string | null>(null);
  const [mergeRows, setMergeRows] = useState<Array<Row | MergeTarget> | null>(null);

  const { data: matchData, loading: matchesLoading } = useWasteStreamRegistrationMatches(
    wasteStream.id,
    !open
  );
  const matches = useMemo(() => matchData?.wasteStreamRegistrationMatches ?? [], [matchData]);

  // A candidate whose registration already owns another stream is a merge, not a
  // link. The server resolves that stream onto the candidate, so the two arrive
  // together and a confirm can never race a separate merge-targets query.
  const mergeTargetByRegistrationId = useMemo(() => {
    const map = new Map<string, MergeTarget>();
    for (const match of matches) {
      if (match.relatedStream) {
        map.set(match.registration.id, match.relatedStream);
      }
    }
    return map;
  }, [matches]);

  const relatedStreamNameByRegistrationId = useMemo(() => {
    const map = new Map<string, string>();
    for (const [registrationId, target] of mergeTargetByRegistrationId) {
      map.set(registrationId, target.name);
    }
    return map;
  }, [mergeTargetByRegistrationId]);

  const selectedMergeTarget = selectedRegistrationId
    ? mergeTargetByRegistrationId.get(selectedRegistrationId)
    : undefined;

  const [createSubmissions] = useMutation(CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION);
  // Covers the whole handler, including the post-mutation refetch, so the
  // button doesn't re-enable while the flow is still finishing.
  const [registeringNew, setRegisteringNew] = useState(false);

  const handleClose = () => {
    setSelectedRegistrationId(null);
    setMergeRows(null);
    onClose();
  };

  const gridWindow = useGridWindow(startDate, endDate);
  const { handleConfirmMatch, confirmingMatch, selectedNeedsUpdate } = useConfirmMatchAction({
    wasteStream,
    facilityId,
    tenantId,
    gridWindow,
    matches,
    selectedRegistrationId,
    selectedMergeTarget,
    setMergeRows,
    handleClose,
    alerts,
    client,
  });

  const handleRegisterNew = async () => {
    setRegisteringNew(true);
    try {
      // Reserve the tab synchronously, in this click's gesture — the create
      // mutation and grid refetch below both await, so a `window.open` fired
      // after them lands too late and gets popup-blocked in Safari.
      await openInNewTabFromPromise(async () => {
        const result = await createSubmissions({
          variables: {
            input: {
              wasteStreamIds: [wasteStream.id],
            },
          },
        });
        const summary = result.data?.createWasteStreamRegistrationSubmissions;
        const failed = summary?.failed ?? 0;
        const created = summary?.created ?? 0;
        const skipped = summary?.skipped ?? 0;
        if (failed > 0) {
          alerts.error("Failed to create the registration.");
          return undefined;
        }
        await refetchWasteStreamGridQueries(client, facilityId);
        // The server skips (rather than fails) a stream that already has an
        // open registration submission — surface that instead of a false
        // "created", and open that existing submission the same as a fresh one.
        const forThisStream = (ref: { wasteStreamId: string }) =>
          ref.wasteStreamId === wasteStream.id;
        const submissionId = (
          summary?.createdSubmissions.find(forThisStream) ??
          summary?.skippedSubmissions.find(forThisStream)
        )?.submissionId;
        if (created === 0 && skipped > 0) {
          alerts.info("This stream already has an open registration submission. Opening it.");
        } else {
          alerts.success("Registration created.");
        }
        handleClose();
        return submissionId ? `/o/${tenantId}/waste/submissions/${submissionId}/edit` : undefined;
      });
    } catch (e) {
      alerts.error("Failed to create the registration.", e);
    } finally {
      setRegisteringNew(false);
    }
  };

  // Deep-links to the existing two-step merge wizard, which owns its own
  // dialog chrome — swap this dialog out for it entirely rather than
  // stacking dialogs.
  if (mergeRows) {
    return (
      <WithWasteFacilityScope facilityId={facilityId} startDate={startDate} endDate={endDate}>
        <WasteStreamMergeDialog
          selectedRows={mergeRows}
          onClose={() => {
            setMergeRows(null);
            handleClose();
          }}
        />
      </WithWasteFacilityScope>
    );
  }

  const primaryActionButtonProps =
    tab === "match"
      ? {
          onClick: handleConfirmMatch,
          disabled: !selectedRegistrationId || confirmingMatch,
          loading: confirmingMatch,
          saveText: confirmLabel(selectedMergeTarget, selectedNeedsUpdate),
          hideStartIcon: true,
        }
      : {
          onClick: handleRegisterNew,
          disabled: registeringNew,
          loading: registeringNew,
          saveText: "Create registration",
          hideStartIcon: true,
        };

  const helpText =
    tab === "match"
      ? "Select an existing state registration for this waste stream. If the state registration selected has another waste stream, this waste stream will be merged into it."
      : "Create a Submission to register this waste stream with the state. This waste stream's state registration will show as In Progress.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // Fixed height so switching Match <-> Register new doesn't resize and
      // re-center the dialog — the (taller) match list scrolls within it.
      PaperProps={{ sx: { height: "80vh" } }}
      title="State Registration"
      primaryActionButtonProps={primaryActionButtonProps}
      stickyHeaderContent={
        <Stack spacing={1.5} sx={{ pb: 1 }}>
          <WithWasteFacilityScope facilityId={facilityId} startDate={startDate} endDate={endDate}>
            <CurrentStreamSummary
              wasteStream={wasteStream}
              facilityName={facilityName}
              state={state}
            />
          </WithWasteFacilityScope>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={tab}
            onChange={(_event, value: Tab | null) => {
              if (value) setTab(value);
            }}
          >
            <ToggleButton value="match">Match existing</ToggleButton>
            <ToggleButton value="new">Register new</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" color="text.secondary">
            {helpText}
          </Typography>
        </Stack>
      }
    >
      {tab === "match" && (
        <MatchTab
          matches={matches}
          loading={matchesLoading}
          selectedRegistrationId={selectedRegistrationId}
          onSelect={setSelectedRegistrationId}
          relatedStreamNameByRegistrationId={relatedStreamNameByRegistrationId}
        />
      )}
      {tab === "new" && (
        <RegisterNewTab
          facilityName={facilityName}
          state={state}
          title={buildWasteStreamRegistrationTitle(state, wasteStream.name)}
        />
      )}
    </Dialog>
  );
}
